/**
 * assets/help-me-choose.js — Shiro Matcha「Help me choose」診断
 *
 * 1. SHIRO_HMC_CONFIG : 設問・選択肢・出口マッピング（ここだけ編集すれば結果が変わる）
 * 2. recommend()      : 回答 → 表示カード（純関数。test/recommend.test.js で検証）
 * 3. <help-me-choose> : モーダル内の設問 UI（sections/collection-help-me-choose.liquid が描画）
 *
 * 注意: matrix / starterKits / fallbackSet / giftMatrix に出てくる handle は、Liquid 側 `hmc_handles` に
 *       含まれている必要がある（含まれていない handle のカードは表示されずスキップされる）。
 *
 * 2026-10-19 改修（先方 10/17 修正案）
 *  - 最初に「Who is it for?」（For me／As a gift）。For me＝Q1→Q2→Q3、As a gift＝G1→G2
 *  - Q3 を経験→飲む頻度（3択）。value は計測の連続性のため beginner／weekly／daily
 *  - For me の表示順：beginner＝Kit→Best match→Can't decide→Also great／それ以外＝Best match→Can't decide→Also great
 *  - Best match の初期選択：beginner・weekly＝50g 単品／daily＝250g＋定期便（250g が定期便の対象外なら 50g＋定期便）
 *
 * 2026-10-27 改修（先方 10/24 相談 ①②）
 *  - ② 選択肢をタップしたら次の質問へ自動で進む（Continue なし・Back は残す・最後の質問は結果へ）
 *  - ① 結果カードのボタンを Add to cart に（画像・商品名は商品ページへ）。入れる商品は purchasePlan() が決める
 *       daily の Best match だけ One-time / Subscribe & save 15% を切り替えられる（初期は One-time）
 *       eGift Card は受取人の入力が商品ページにあるので、カートには入れず商品ページへ
 *  - ③ 選択肢にアイコン（HMC_ICONS。各選択肢の icon で指定）
 */
(function () {
  'use strict';

  var LONG_NOTE = 'Drinking daily or sharing at home? 250g lasts about 2 months (4g a day). Subscribe & save 15% and never run out.';
  var VALUE_NOTE = 'Better value per cup. Subscribe & save 15% and never run out.';

  // ③ 選択肢のアイコン（2026-10-27。test/icons-preview.html で作成した 48×48 の SVG の中身）
  // stroke は currentColor（#1b522c）。SVG の fill 属性では CSS 変数が効かないことがあるので塗りは色を直接書く
  var L = '#bcd99a'; // 淡い抹茶色
  var R = '#dcc59c'; // Roasty だけ焙じ色
  var HMC_ICONS = {
    // Q0 Who is it for?
    me: // 湯気がハートのカップ
      '<path d="M24 17.5c-3.6-2.6-4.6-4.6-4.6-6 0-1.4 1-2.4 2.3-2.4 1 0 1.8.6 2.3 1.4.5-.8 1.3-1.4 2.3-1.4 1.3 0 2.3 1 2.3 2.4 0 1.4-1 3.4-4.6 6z" fill="' + L + '"/>' +
      '<path d="M12.5 22h21v8.5a7.5 7.5 0 0 1-7.5 7.5h-6a7.5 7.5 0 0 1-7.5-7.5z" fill="' + L + '"/>' +
      '<path d="M33.5 24.5h2.2a3.8 3.8 0 0 1 0 7.6h-2.6"/>',
    gift: // ギフトボックス
      '<rect x="12" y="22" width="24" height="15" rx="1.5" fill="' + L + '"/>' +
      '<rect x="10" y="16.5" width="28" height="5.5" rx="1.5"/>' +
      '<path d="M24 16.5V37"/>' +
      '<path d="M24 16.5c-2.6-4.8-8.2-4.6-7-1.3.7 1.8 3.8 1.3 7 1.3zm0 0c2.6-4.8 8.2-4.6 7-1.3-.7 1.8-3.8 1.3-7 1.3z"/>',

    // Q1 How do you drink it?
    straight: // 茶碗と茶筅
      '<path d="M10 25h28c0 7-6.3 12-14 12s-14-5-14-12z" fill="' + L + '"/>' +
      '<path d="M19 37.5h10"/>' +
      '<path d="M24 6v6.5"/>' +
      '<path d="M21 12.5h6l2.2 9.5H18.8z" fill="#fff"/>' +
      '<path d="M22.3 13.5 21.2 21.5M24 13.5v8M25.7 13.5l1.1 8"/>',
    latte: // ラテのグラスとストロー
      '<path d="M17.6 26.5c2.2-1.4 4.4-1.4 6.4 0s4.2 1.4 6.4 0L29.6 36a2 2 0 0 1-2 1.8h-7.2a2 2 0 0 1-2-1.8z" fill="' + L + '" stroke="none"/>' +
      '<path d="M15.5 13h17l-2 23.2a2 2 0 0 1-2 1.8h-9a2 2 0 0 1-2-1.8z"/>' +
      '<path d="M17.6 26.5c2.2-1.4 4.4-1.4 6.4 0s4.2 1.4 6.4 0"/>' +
      '<path d="M27.5 7.5 26 20"/>',
    both: // 茶碗とグラス
      '<path d="M7 28h17c0 5-3.8 8.5-8.5 8.5S7 33 7 28z" fill="' + L + '"/>' +
      '<path d="M12 37h7"/>' +
      '<path d="M28.6 27.5h9.4l-.6 7.6a1.6 1.6 0 0 1-1.6 1.4h-5a1.6 1.6 0 0 1-1.6-1.4z" fill="' + L + '" stroke="none"/>' +
      '<path d="M27.5 15h11.6l-1.5 20.2a1.6 1.6 0 0 1-1.6 1.4h-5.4a1.6 1.6 0 0 1-1.6-1.4z"/>' +
      '<path d="M28.6 27.5h9.4"/>',

    // Q2 What flavour do you prefer?
    umami: // しずくのついた茶葉
      '<path d="M14 36c0-13 8.5-21.5 23-22.5C37 27 28.5 35.5 14 36z" fill="' + L + '"/>' +
      '<path d="M14 36 30 20"/>' +
      '<path d="M15.5 9.5s-4.2 4.8-4.2 7.4a4.2 4.2 0 0 0 8.4 0c0-2.6-4.2-7.4-4.2-7.4z" fill="' + L + '"/>',
    balanced: // てんびん
      '<path d="M24 12.5V36M17.5 37h13M11 16.5h26"/>' +
      '<circle cx="24" cy="11" r="1.6"/>' +
      '<path d="M11 16.5 7 27M11 16.5 15 27M37 16.5 33 27M37 16.5 41 27"/>' +
      '<path d="M6 27h10a5 3.6 0 0 1-10 0zM32 27h10a5 3.6 0 0 1-10 0z" fill="' + L + '"/>',
    light: // 風と茶葉
      '<path d="M22 33c0-9.5 6-15.5 15-16-.2 9.4-6.2 15.4-15 16z" fill="' + L + '"/>' +
      '<path d="M22 33 32 22.5"/>' +
      '<path d="M7 19.5h10a3 3 0 1 0-3-3"/>' +
      '<path d="M7 26h10"/>' +
      '<path d="M9 32.5h7"/>',
    roasty: // 焙じた茶葉と米粒
      '<path d="M10 32c0-11.5 8-19 20-20-.5 12-8 19.5-20 20z" fill="' + R + '"/>' +
      '<path d="M10 32 25 17"/>' +
      '<ellipse cx="30.5" cy="35" rx="3.2" ry="2" transform="rotate(-25 30.5 35)" fill="#fff"/>' +
      '<ellipse cx="39" cy="30" rx="3.2" ry="2" transform="rotate(25 39 30)" fill="#fff"/>' +
      '<ellipse cx="38" cy="39" rx="3.2" ry="2" transform="rotate(-10 38 39)" fill="#fff"/>',

    // Q3 How often do you drink matcha?
    beginner: // 芽
      '<path d="M12 37.5h24M24 37.5V23"/>' +
      '<path d="M24 27.5c-6 0-10.5-4-10.5-9.5 6 0 10.5 3.5 10.5 9.5z" fill="' + L + '"/>' +
      '<path d="M24 23c0-6.5 4.5-11 11.5-11 0 6.5-4.5 11-11.5 11z" fill="' + L + '"/>',
    weekly: // カレンダーに点3つ
      '<path d="M11 21v-4a3 3 0 0 1 3-3h20a3 3 0 0 1 3 3v4z" fill="' + L + '" stroke="none"/>' +
      '<rect x="11" y="14" width="26" height="23" rx="3"/>' +
      '<path d="M11 21h26M18 10.5v6M30 10.5v6"/>' +
      '<circle cx="18" cy="29" r="2" fill="currentColor" stroke="none"/>' +
      '<circle cx="24" cy="29" r="2" fill="currentColor" stroke="none"/>' +
      '<circle cx="30" cy="29" r="2" fill="currentColor" stroke="none"/>',
    daily: // 家と太陽
      '<path d="M11.5 37.5V26L23 16.5 34.5 26v11.5z" fill="' + L + '"/>' +
      '<path d="M9 28 23 16.5 37 28"/>' +
      '<path d="M19.5 37.5v-7h7v7"/>' +
      '<circle cx="37" cy="11" r="3.2"/>' +
      '<path d="M37 4.5v1.8M37 15.7v1.8M30.5 11h1.8M41.7 11h1.8M32.4 6.4l1.3 1.3M40.3 14.3l1.3 1.3M41.6 6.4l-1.3 1.3"/>',

    // G1 What do they enjoy?
    coffee: // 湯気の立つマグ
      '<path d="M11 21h21v9.5a7.5 7.5 0 0 1-7.5 7.5h-6a7.5 7.5 0 0 1-7.5-7.5z" fill="' + L + '"/>' +
      '<path d="M32 23.5h2.2a3.8 3.8 0 0 1 0 7.6H31.6"/>' +
      '<path d="M17 9c-1.8 2.2 1.8 3.8 0 6.5M23 9c-1.8 2.2 1.8 3.8 0 6.5M29 9c-1.8 2.2 1.8 3.8 0 6.5"/>',
    tea: // 急須
      '<path d="M12 23h22c0 8.5-4.9 14-11 14s-11-5.5-11-14z" fill="' + L + '"/>' +
      '<path d="M16.5 23c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6"/>' +
      '<circle cx="23" cy="14.6" r="1.6"/>' +
      '<path d="M12.6 26.5c-3.2 0-5.4-2.2-6.4-5.5"/>' +
      '<path d="M33.6 25.5c4 0 5.6 2 5.6 4.4s-2.2 4.6-6.4 4.6"/>',
    unsure: // きらめきと「?」
      '<path d="M19 9c1 7.2 3 9.2 10 10-7 .8-9 2.8-10 10-1-7.2-3-9.2-10-10 7-.8 9-2.8 10-10z" fill="' + L + '"/>' +
      '<path d="M31 30.5a4.3 4.3 0 1 1 6 4c-1.1.5-1.7 1.3-1.7 2.4v.8"/>' +
      '<circle cx="35.3" cy="41.6" r="1.3" fill="currentColor" stroke="none"/>',

    // G2 What's your budget?
    under100: // ハートのギフトタグ
      '<path d="M13 13.5h15.5L38 24l-9.5 10.5H13a2 2 0 0 1-2-2v-17a2 2 0 0 1 2-2z" fill="' + L + '"/>' +
      '<circle cx="31" cy="24" r="1.9" fill="#fff"/>' +
      '<path d="M19.5 28.8c-3.4-2.4-4.3-4.3-4.3-5.6 0-1.3.9-2.2 2.1-2.2.9 0 1.7.5 2.2 1.3.5-.8 1.3-1.3 2.2-1.3 1.2 0 2.1.9 2.1 2.2 0 1.3-.9 3.2-4.3 5.6z"/>',
    over100: // リボンの大きなギフトときらめき
      '<rect x="8" y="22" width="28" height="16" rx="1.5" fill="' + L + '"/>' +
      '<rect x="6" y="16" width="32" height="6" rx="1.5"/>' +
      '<path d="M22 16v22"/>' +
      '<path d="M22 16c-3-5.6-9.6-5.4-8.2-1.5.8 2.1 4.5 1.5 8.2 1.5zm0 0c3-5.6 9.6-5.4 8.2-1.5-.8 2.1-4.5 1.5-8.2 1.5z"/>' +
      '<path d="M40.5 7c.4 2.8 1.2 3.6 4 4-2.8.4-3.6 1.2-4 4-.4-2.8-1.2-3.6-4-4 2.8-.4 3.6-1.2 4-4z" fill="' + L + '"/>'
  };

  var SHIRO_HMC_CONFIG = {
    questions: [
      {
        id: 'for',
        title: 'Who is it for?',
        options: [
          { value: 'me', icon: 'me', label: 'For me', hint: 'Find my everyday matcha' },
          { value: 'gift', icon: 'gift', label: 'As a gift', hint: 'For someone special' }
        ]
      },
      {
        id: 'use',
        title: 'How do you drink it?',
        showWhen: { for: 'me' },
        options: [
          { value: 'straight', icon: 'straight', label: 'Straight', hint: 'Usucha / thin tea, whisked with water' },
          { value: 'latte', icon: 'latte', label: 'Latte', hint: 'With milk or plant-based milk' },
          { value: 'both', icon: 'both', label: 'Both', hint: 'Straight on some days, latte on others' }
        ]
      },
      {
        id: 'taste',
        title: 'What flavour do you prefer?',
        showWhen: { for: 'me' },
        options: [
          { value: 'umami', icon: 'umami', label: 'Rich Umami & Smooth', hint: 'Deep savoury sweetness, silky finish' },
          { value: 'balanced', icon: 'balanced', label: 'Balanced & Versatile', hint: 'Umami and body in harmony' },
          { value: 'light', icon: 'light', label: 'Light & Refreshing', hint: 'Clean, easy-drinking' },
          // Hojicha・Genmai・Wakocha はラテ向けの商品なので、Straight を選んだ人には出さない
          { value: 'roasty', icon: 'roasty', label: 'Roasty, Nutty or Mellow', hint: 'Hojicha · Genmaicha · Wakocha', hideWhen: { use: 'straight' } }
        ]
      },
      {
        id: 'exp',
        title: 'How often do you drink matcha?',
        showWhen: { for: 'me' },
        options: [
          { value: 'beginner', icon: 'beginner', label: 'Just starting out', hint: 'New to matcha, or just getting started' },
          { value: 'weekly', icon: 'weekly', label: 'A few times a week', hint: 'When I feel like a treat' },
          { value: 'daily', icon: 'daily', label: 'Every day or shared at home', hint: 'My daily ritual, or for the whole household' }
        ]
      },
      {
        id: 'gift_pref',
        title: 'What do they enjoy?',
        showWhen: { for: 'gift' },
        options: [
          { value: 'latte', icon: 'coffee', label: 'Lattes & coffee', hint: 'Café drinks, milk-based' },
          { value: 'tea', icon: 'tea', label: 'Tea & calm moments', hint: 'Prefers tea, gentle flavours' },
          { value: 'unsure', icon: 'unsure', label: 'Not sure', hint: 'Something new to try' }
        ]
      },
      {
        id: 'budget',
        title: "What's your budget?",
        showWhen: { for: 'gift' },
        options: [
          { value: 'under100', icon: 'under100', label: 'Under $100', hint: 'A thoughtful little gift' },
          { value: 'over100', icon: 'over100', label: '$100 or more', hint: 'A special gift' }
        ]
      }
    ],

    // taste -> use -> [Best match, Also great]
    matrix: {
      umami: {
        straight: ['matcha-極-kiwami', 'matcha-凛-rin'],
        latte: ['matcha-凛-rin', 'matcha-侘-wabi'],
        both: ['matcha-凛-rin', 'matcha-極-kiwami']
      },
      balanced: {
        straight: ['matcha-凛-rin', 'matcha-極-kiwami'],
        latte: ['matcha-侘-wabi', 'matcha-凛-rin'],
        both: ['matcha-凛-rin', 'matcha-侘-wabi']
      },
      light: {
        straight: ['matcha-凛-rin', 'matcha-侘-wabi'],
        latte: ['matcha-侘-wabi', 'matcha-凛-rin'],
        both: ['matcha-侘-wabi', 'matcha-凛-rin']
      },
      roasty: {
        latte: ['genmai-matcha', 'japanese-black-tea'],
        both: ['hojicha', 'genmai-matcha']
      }
    },

    // 特定の組み合わせだけ matrix を上書き
    overrides: [
      { when: { use: 'latte', taste: 'umami', exp: 'beginner' }, picks: ['matcha-侘-wabi', 'matcha-凛-rin'] }
    ],

    // Just starting out のとき Best match に対応する Starter Kit（Shaker + X）
    starterKits: {
      'matcha-侘-wabi': 'shaker-set-wabi',
      'matcha-凛-rin': 'shaker-set-rin',
      'matcha-極-kiwami': 'shaker-set-kiwami',
      hojicha: 'shaker-set-hojicha',
      'genmai-matcha': 'shaker-set-genmai',
      'japanese-black-tea': 'shaker-set-wakocha'
    },

    // 「Can't decide?」カード
    fallbackSet: { matcha: 'matcha-selection-set', tea: '50g-4-pack-set' },

    // この taste を選んだら group = 'tea'
    teaTastes: ['roasty'],

    // Every day or shared at home のとき Best match のカードに出す案内文（taste -> use。先方シート M 列の原文）
    dailyNotes: {
      umami: {
        straight: 'Drinking daily? Subscribe & save 15% and never run out.',
        latte: LONG_NOTE,
        both: LONG_NOTE
      },
      balanced: { straight: LONG_NOTE, latte: LONG_NOTE, both: VALUE_NOTE },
      light: { straight: VALUE_NOTE, latte: VALUE_NOTE, both: VALUE_NOTE },
      roasty: { latte: VALUE_NOTE, both: VALUE_NOTE }
    },

    // As a gift：gift_pref -> budget -> [① Best match, ② Also great, ③ Also great（無い場合は null）, ④ Can't decide?]
    giftMatrix: {
      latte: {
        under100: ['shaker-set-wabi', 'shaker-set-rin', 'matcha-selection-set', 'shiro-matcha-egift-card'],
        over100: ['matcha-selection-set', '50g-4-pack-set', null, 'shiro-matcha-egift-card']
      },
      tea: {
        under100: ['shaker-set-hojicha', 'shaker-set-wakocha', '50g-4-pack-set', 'shiro-matcha-egift-card'],
        over100: ['50g-4-pack-set', 'matcha-selection-set', null, 'shiro-matcha-egift-card']
      },
      unsure: {
        // ② と ④ がどちらも eGift Card → 重複は1枚にまとめる（② の位置に表示）
        under100: ['shaker-set-wabi', 'shiro-matcha-egift-card', 'matcha-selection-set', 'shiro-matcha-egift-card'],
        over100: ['matcha-selection-set', '50g-4-pack-set', null, 'shiro-matcha-egift-card']
      }
    },

    // 計測用の商品種別（kit＝starterKits の値／set＝ここに挙げた handle／それ以外＝single）
    setHandles: ['matcha-selection-set', '50g-4-pack-set', 'shiro-matcha-egift-card']
  };

  var ROLE_LABELS = {
    primary: 'Best match',
    secondary: 'Also great',
    kit: 'Start with the kit',
    set: "Can't decide?"
  };

  function matchesWhen(when, answers) {
    return Object.keys(when).every(function (k) { return answers[k] === when[k]; });
  }

  // showWhen（例: { for: 'me' }）に当たらない質問は、その回答の経路では表示しない
  function activeQuestions(config, answers) {
    return config.questions.filter(function (q) { return !q.showWhen || matchesWhen(q.showWhen, answers || {}); });
  }

  // hideWhen（例: { use: 'straight' }）に当たる選択肢は、その回答のときは表示しない
  function visibleOptions(question, answers) {
    return question.options.filter(function (o) { return !o.hideWhen || !matchesWhen(o.hideWhen, answers || {}); });
  }

  function isValidAnswer(question, value, answers) {
    return visibleOptions(question, answers).some(function (o) { return o.value === value; });
  }

  function productType(config, handle) {
    var kits = Object.keys(config.starterKits).map(function (k) { return config.starterKits[k]; });
    if (kits.indexOf(handle) >= 0) return 'kit';
    if ((config.setHandles || []).indexOf(handle) >= 0) return 'set';
    return 'single';
  }

  /**
   * @param {{for:string, use?:string, taste?:string, exp?:string, gift_pref?:string, budget?:string}} answers
   * @param {object} [config]
   * @returns {{group:'matcha'|'tea'|'gift', cards:{handle:string, role:string, type:string, position:number, select?:string, note?:string}[]}|null}
   *   cards は表示順。select は Best match の初期選択（'50g'＝50g 単品／'daily'＝250g＋定期便、無ければ 50g＋定期便）
   */
  function recommend(answers, config) {
    config = config || SHIRO_HMC_CONFIG;
    if (!answers) return null;
    var questions = activeQuestions(config, answers);
    if (!questions.length || questions[0].id !== 'for') return null;
    for (var q = 0; q < questions.length; q++) {
      if (!isValidAnswer(questions[q], answers[questions[q].id], answers)) return null;
    }

    var cards = [];
    var seen = {};
    function push(handle, role, extra) {
      if (!handle || seen[handle]) return;
      seen[handle] = true;
      var card = { handle: handle, role: role, type: productType(config, handle) };
      if (extra) Object.keys(extra).forEach(function (k) { card[k] = extra[k]; });
      cards.push(card);
    }
    function finish(group) {
      cards.forEach(function (c, i) { c.position = i + 1; });
      return { group: group, cards: cards };
    }

    if (answers['for'] === 'gift') {
      var giftRow = config.giftMatrix[answers.gift_pref];
      var gift = giftRow && giftRow[answers.budget];
      if (!gift) return null;
      push(gift[0], 'primary');
      push(gift[1], 'secondary');
      push(gift[2], 'secondary'); // ③ は Also great のラベルで表示
      push(gift[3], 'set');
      return finish('gift');
    }

    var picks = null;
    for (var i = 0; i < (config.overrides || []).length; i++) {
      if (matchesWhen(config.overrides[i].when, answers)) { picks = config.overrides[i].picks; break; }
    }
    if (!picks) {
      var row = config.matrix[answers.taste];
      picks = row && row[answers.use];
    }
    if (!picks || !picks.length) return null;

    var group = (config.teaTastes || []).indexOf(answers.taste) >= 0 ? 'tea' : 'matcha';
    var primaryExtra = { select: '50g' };
    if (answers.exp === 'daily') {
      var noteRow = config.dailyNotes[answers.taste];
      primaryExtra = { select: 'daily', note: (noteRow && noteRow[answers.use]) || '' };
    }

    if (answers.exp === 'beginner') push(config.starterKits[picks[0]], 'kit');
    push(picks[0], 'primary', primaryExtra);
    push(config.fallbackSet[group], 'set');
    push(picks[1], 'secondary');
    return finish(group);
  }

  // 定期便のプラン名（例: "Every month - 15% off"）からお届け間隔だけ取り出す
  function planInterval(name) {
    return String(name || '').split(' - ')[0].trim();
  }

  /**
   * 結果カードの Add to cart で入れる商品（2026-10-27）
   * @param {{select?:string}|null} item  recommend() のカード
   * @param {object} d  カードの data 属性（sections/collection-help-me-choose.liquid が出力。値は文字列）
   *   gift … eGift Card なら 'true'
   *   vDefault / vDefaultPrice / vDefaultAvailable … 既定のバリアント（キット・セットなどサイズ違いが無い商品）
   *   v50 / v50Price / v50Available / v50Plan / v50PlanPrice / v50PlanName … 50g（250g も同じ形）
   * @returns {{mode:'gift'}|{mode:'add', onetime:object, subscribe:object|null}}
   *   onetime / subscribe = { variant, size, price, available, plan?, interval? }
   *   subscribe があるときだけ、カード内に One-time / Subscribe の切り替えを出す（初期は One-time）
   */
  function purchasePlan(item, d) {
    d = d || {};
    if (d.gift === 'true') return { mode: 'gift' };
    function size(g) {
      var id = d['v' + g];
      if (!id) return null;
      return { variant: id, size: g + 'g', price: d['v' + g + 'Price'] || '', available: d['v' + g + 'Available'] !== 'false' };
    }
    var v50 = size('50');
    var v250 = size('250');
    // パウダーは 50g、サイズ違いの無い商品は既定のバリアント
    var onetime = v50 || { variant: d.vDefault || '', size: '', price: d.vDefaultPrice || '', available: !!d.vDefault && d.vDefaultAvailable !== 'false' };
    var subscribe = null;
    if (item && item.select === 'daily') {
      // Every day の Best match：One-time は 250g（無い・在庫切れなら 50g。Kiwami は 250g が無いので 50g）
      if (v250 && (v250.available || !v50)) onetime = v250;
      // 定期便は 250g にあれば 250g、無ければ 50g（2026-10 時点で定期便があるのは 50g だけ）
      var g = v250 && d.v250Plan ? '250' : (v50 && d.v50Plan ? '50' : '');
      if (g) {
        var base = g === '250' ? v250 : v50;
        subscribe = {
          variant: base.variant,
          size: base.size,
          price: d['v' + g + 'PlanPrice'] || base.price,
          available: base.available,
          plan: d['v' + g + 'Plan'],
          interval: planInterval(d['v' + g + 'PlanName'])
        };
      }
    }
    return { mode: 'add', onetime: onetime, subscribe: subscribe };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { SHIRO_HMC_CONFIG: SHIRO_HMC_CONFIG, HMC_ICONS: HMC_ICONS, recommend: recommend, purchasePlan: purchasePlan, ROLE_LABELS: ROLE_LABELS, activeQuestions: activeQuestions, visibleOptions: visibleOptions };
  }
  if (typeof window === 'undefined' || !window.customElements) return;

  window.SHIRO_HMC_CONFIG = window.SHIRO_HMC_CONFIG || SHIRO_HMC_CONFIG;
  window.shiroHmcRecommend = recommend;

  /* ------------------------------------------------------------------ */
  /* UI                                                                  */
  /* ------------------------------------------------------------------ */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // このサイトは send_to 未指定の gtag イベントが破棄されるため、両測定IDへ明示送信する
  var GA4_IDS = ['G-ZKEEJ9JGTF', 'G-02769480V7'];
  // 結果カードのクリックを、その後のカート追加・購入に紐付けるためのキー（snippets/hmc-attribution.liquid と共通）
  var ATTR_KEY = 'sm_hmc_attr';

  function sendGa(event, params) {
    for (var i = 0; i < GA4_IDS.length; i++) {
      window.gtag('event', event, Object.assign({ send_to: GA4_IDS[i], transport_type: 'beacon' }, params));
    }
  }

  // gtag は GTM 読込後に定義されるため、未定義なら最大10秒待ってから送る
  function withGtag(fn) {
    if (typeof window.gtag === 'function') { fn(); return; }
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (typeof window.gtag === 'function') { clearInterval(timer); fn(); }
      else if (tries >= 40) clearInterval(timer);
    }, 250);
  }

  // before: gtag 送信の直前に実行する処理（ユーザープロパティの設定など）
  function track(event, params, before) {
    params = params || {};
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: event }, params));
    } catch (e) { /* noop */ }
    withGtag(function () {
      if (before) before();
      sendGa(event, params);
    });
  }

  function saveAttr(attr) {
    try {
      attr.ts = Date.now();
      window.sessionStorage.setItem(ATTR_KEY, JSON.stringify(attr));
    } catch (e) { /* noop */ }
  }

  function norm(s) { return String(s || '').normalize ? String(s || '').normalize('NFC') : String(s || ''); }

  var HelpMeChoose = function () { return Reflect.construct(HTMLElement, [], HelpMeChoose); };
  HelpMeChoose.prototype = Object.create(HTMLElement.prototype);
  HelpMeChoose.prototype.constructor = HelpMeChoose;
  Object.setPrototypeOf(HelpMeChoose, HTMLElement);

  HelpMeChoose.prototype.connectedCallback = function () {
    if (this.initialized) return;
    this.initialized = true;
    this.config = window.SHIRO_HMC_CONFIG;
    this.stepsEl = this.querySelector('[data-hmc-steps]');
    this.resultsEl = this.querySelector('[data-hmc-results]');
    this.footerEl = this.querySelector('[data-hmc-footer]');
    this.titleId = this.dataset.titleId || 'HelpMeChooseTitle';
    this.labels = {
      primary: this.dataset.labelPrimary || ROLE_LABELS.primary,
      secondary: this.dataset.labelSecondary || ROLE_LABELS.secondary,
      kit: this.dataset.labelKit || ROLE_LABELS.kit,
      set: this.dataset.labelSet || ROLE_LABELS.set
    };
    this.text = {
      introTitle: this.dataset.introTitle || 'Help me choose',
      introText: this.dataset.introText || '',
      resultTitle: this.dataset.resultTitle || 'Our pick for you',
      cont: this.dataset.continueLabel || 'Continue',
      back: this.dataset.backLabel || 'Back',
      restart: this.dataset.restartLabel || 'Start over',
      progress: this.dataset.progressLabel || 'Question {n} of {total}',
      add: this.dataset.addLabel || 'Add to cart',
      adding: this.dataset.addingLabel || 'Adding…',
      added: this.dataset.addedLabel || 'Added ✓',
      addedStatus: this.dataset.addedStatus || 'Added to your cart',
      soldOut: this.dataset.soldOutLabel || 'Sold out',
      addError: this.dataset.addError || 'Sorry, we couldn’t add this to your cart. Please try again.'
    };
    this.state = { step: 0, answers: {} };
    // 診断の入口（collection＝コレクションページのボタン／home＝TOPページのボタン／deeplink＝#help-me-choose 付きリンク）
    this.source = this.dataset.source || 'collection';

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    // タップ・クリックで選んだときだけ自動で次へ進める（矢印キーで選択を動かしたときは進めない）
    // 押した選択肢も覚える（別のボタンを押した直後の矢印キーをタップと取り違えないため）
    this.addEventListener('pointerdown', function (event) {
      this.pointerAt = Date.now();
      this.pointerOption = event.target.closest ? event.target.closest('.hmc-option') : null;
    }.bind(this));
    this.watchModal();
    this.render(false);
    this.autoOpen();
  };

  // URL が #help-me-choose のときはモーダルを自動で開く（TOP のレーダーセクション・ヘッダーのボタンからの導線）
  // 同じページ上でリンクを押した場合（ハッシュだけ変わる）にも開くよう hashchange も監視する
  HelpMeChoose.prototype.autoOpen = function () {
    var self = this;
    var modal = this.closest('modal-dialog');
    if (!modal) return;
    var opener = document.querySelector('[data-hmc-open]');
    var openIfHash = function () {
      if (window.location.hash !== '#help-me-choose') return;
      self.source = 'deeplink';
      window.customElements.whenDefined('modal-dialog').then(function () {
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        if (modal.hasAttribute('open') || typeof modal.show !== 'function') return;
        modal.show(opener);
      });
    };
    window.addEventListener('hashchange', openIfHash);
    openIfHash();
  };

  // モーダルの開閉に合わせて body にクラスを付け、チャットランチャー等を隠す
  HelpMeChoose.prototype.watchModal = function () {
    var modal = this.closest('modal-dialog');
    if (!modal || typeof MutationObserver === 'undefined') return;
    var sync = function () {
      document.body.classList.toggle('hmc-modal-open', modal.hasAttribute('open'));
    };
    new MutationObserver(sync).observe(modal, { attributes: true, attributeFilter: ['open'] });
    sync();
  };

  HelpMeChoose.prototype.onClick = function (event) {
    // 画像・商品名（eGift はボタンも）→ 商品ページへ
    var view = event.target.closest('[data-hmc-view]');
    if (view) {
      this.trackCard(view.closest('.hmc-card'), 'view');
      return;
    }
    var add = event.target.closest('[data-hmc-add]');
    if (add) {
      event.preventDefault();
      this.addToCart(add.closest('.hmc-card'), add);
      return;
    }
    // 選択肢のラベルを押すと、ブラウザが input にも click を送る（change より先に届く）
    var option = event.target.closest('.hmc-option__input');
    if (option) {
      this.onPick(option);
      return;
    }
    var btn = event.target.closest('[data-hmc-action]');
    if (!btn) return;
    event.preventDefault();
    var action = btn.dataset.hmcAction;
    if (action === 'next') this.next();
    else if (action === 'back') this.back();
    else if (action === 'restart') this.restart();
  };

  /**
   * 結果カードのクリックを記録し、その後のカート追加・購入に紐付ける（snippets/hmc-attribution.liquid が読む）
   * action: 'view'＝画像・商品名から商品ページへ／'add'＝カード内で Add to cart
   */
  HelpMeChoose.prototype.trackCard = function (card, action, purchase) {
    var handle = card ? norm(card.dataset.handle) : '';
    var role = card ? card.dataset.role || '' : '';
    var position = card ? Number(card.dataset.position) || 0 : 0;
    var type = card ? card.dataset.type || '' : '';
    var primaryCard = this.result ? this.result.cards.filter(function (c) { return c.role === 'primary'; })[0] : null;
    var primary = primaryCard ? norm(primaryCard.handle) : '';
    // via: カート追加がどこで起きたか（card＝結果カード／pdp＝商品ページ）
    saveAttr({ handle: handle, role: role, primary: primary, source: this.source, position: position, type: type, via: action === 'add' ? 'card' : 'pdp' });
    // 選んだ商品をユーザー単位でも記録（GA4 のユーザー範囲ディメンション hmc_pick）
    track('help_me_choose_click', {
      hmc_handle: handle,
      hmc_role: role,
      hmc_primary: primary,
      hmc_source: this.source,
      hmc_for: this.state.answers['for'] || '',
      hmc_position: position,
      hmc_type: type,
      hmc_action: action,
      hmc_purchase: purchase || ''
    }, function () {
      window.gtag('set', 'user_properties', { hmc_pick: handle });
    });
  };

  HelpMeChoose.prototype.onChange = function (event) {
    var input = event.target;
    if (!input || input.type !== 'radio') return;
    if (input.dataset.hmcPurchase != null) {
      this.refreshCard(input.closest('.hmc-card'), true);
      return;
    }
    if (input.dataset.hmcQuestion) this.setAnswer(input.dataset.hmcQuestion, input.value);
  };

  HelpMeChoose.prototype.setAnswer = function (id, value) {
    var answers = this.state.answers;
    answers[id] = value;
    // 回答を変えたことで、選択済みの別の回答が表示対象外になったら消す
    // （例: Roasty を選んだ後に戻って Straight に変更／For me で答えた後に戻って As a gift に変更）
    var active = activeQuestions(this.config, answers);
    this.config.questions.forEach(function (q) {
      if (!answers[q.id]) return;
      var shown = active.indexOf(q) >= 0 && visibleOptions(q, answers).some(function (o) { return o.value === answers[q.id]; });
      if (!shown) delete answers[q.id];
    });
  };

  // 選んだ状態を少し見せてから次の質問（最後の質問なら結果）へ進む
  var ADVANCE_DELAY = 250;

  HelpMeChoose.prototype.onPick = function (input) {
    if (!input.dataset.hmcQuestion) return;
    var label = input.closest('.hmc-option');
    var byPointer = !!label && label === this.pointerOption && Date.now() - (this.pointerAt || 0) < 1500;
    this.pointerAt = 0;
    this.pointerOption = null;
    if (!byPointer) return; // 矢印キーで選択を動かしただけ → 進めない（Enter／Space で進む）
    this.pick(input);
  };

  HelpMeChoose.prototype.pick = function (input) {
    // 同じ選択肢をもう一度押したときは change が起きないので、ここで回答を保存する
    this.setAnswer(input.dataset.hmcQuestion, input.value);
    Array.prototype.forEach.call(this.stepsEl.querySelectorAll('.hmc-option'), function (o) { o.classList.remove('is-picked'); });
    var label = input.closest('.hmc-option');
    if (label) label.classList.add('is-picked');
    if (this.advanceTimer) return; // 進む途中（二度押し・選び直し）→ 予約済みの1回だけ進む
    var self = this;
    this.advanceTimer = setTimeout(function () {
      self.advanceTimer = null;
      self.next();
    }, ADVANCE_DELAY);
  };

  HelpMeChoose.prototype.onKeydown = function (event) {
    var input = event.target;
    if (!input || input.type !== 'radio' || !input.dataset.hmcQuestion) return;
    if (event.key === ' ') {
      // 選択済みの選択肢では Space を押してもブラウザは click を送らないので、ここで選んで進める
      event.preventDefault();
      input.checked = true;
      this.pick(input);
    } else if (event.key === 'Enter' && input.checked) {
      event.preventDefault();
      this.next();
    }
  };

  // 今の回答で表示する質問（Who is it for? の回答で For me／As a gift の経路が決まる）。
  // まだ答えていないときは For me の経路で数える（進捗の分母を 4 にするため）
  HelpMeChoose.prototype.questionsForPath = function () {
    var answers = this.state.answers;
    return activeQuestions(this.config, answers['for'] ? answers : { 'for': 'me' });
  };

  HelpMeChoose.prototype.currentQuestion = function () {
    return this.questionsForPath()[this.state.step - 1] || null;
  };

  HelpMeChoose.prototype.next = function () {
    var total = this.questionsForPath().length;
    if (this.state.step === 0) {
      track('help_me_choose_start', { hmc_source: this.source });
    } else {
      var q = this.currentQuestion();
      if (q && !this.state.answers[q.id]) return; // 未回答
    }
    if (this.state.step > total) return; // 既に結果表示中
    this.state.step += 1;
    this.render(true);
  };

  HelpMeChoose.prototype.back = function () {
    if (this.state.step === 0) return;
    this.state.step -= 1;
    this.render(true);
  };

  HelpMeChoose.prototype.restart = function () {
    this.state = { step: 0, answers: {} };
    this.render(true);
  };

  HelpMeChoose.prototype.render = function (moveFocus) {
    // 自動で進む予約が残っていたら取り消す（予約中に Back を押した場合など）
    if (this.advanceTimer) {
      clearTimeout(this.advanceTimer);
      this.advanceTimer = null;
    }
    var questions = this.questionsForPath();
    var total = questions.length;
    var step = this.state.step;
    var html;

    if (step === 0) {
      html =
        '<div class="hmc-step hmc-step--intro">' +
        '<h2 class="hmc-step__title" id="' + esc(this.titleId) + '" tabindex="-1">' + esc(this.text.introTitle) + '</h2>' +
        (this.text.introText ? '<p class="hmc-step__text">' + esc(this.text.introText) + '</p>' : '') +
        '<div class="hmc-step__actions">' +
        '<button type="button" class="button hmc-button" data-hmc-action="next">' + esc(this.text.cont) + '</button>' +
        '</div></div>';
      this.hideResults();
    } else if (step <= total) {
      var q = questions[step - 1];
      var current = this.state.answers[q.id];
      var progress = this.text.progress.replace('{n}', step).replace('{total}', total);
      html =
        '<div class="hmc-step hmc-step--question" data-step="' + step + '">' +
        '<p class="hmc-step__progress">' + esc(progress) + '</p>' +
        '<fieldset class="hmc-step__fieldset">' +
        '<legend class="hmc-step__title" id="' + esc(this.titleId) + '" tabindex="-1">' + esc(q.title) + '</legend>' +
        '<div class="hmc-options" role="radiogroup">' +
        visibleOptions(q, this.state.answers).map(function (o, idx) {
          var id = 'hmc-' + q.id + '-' + o.value;
          // ③ アイコンがある選択肢は、左に丸い背景つきのアイコン・右に文言（無い選択肢は従来どおりラジオの丸）
          var svg = o.icon && HMC_ICONS[o.icon];
          return (
            '<label class="hmc-option' + (svg ? ' hmc-option--icon' : '') + '" for="' + id + '">' +
            '<input class="hmc-option__input" type="radio" id="' + id + '" name="hmc-' + esc(q.id) + '" value="' + esc(o.value) + '" data-hmc-question="' + esc(q.id) + '"' +
            (current === o.value ? ' checked' : '') + '>' +
            '<span class="hmc-option__body">' +
            (svg
              ? '<span class="hmc-option__icon" aria-hidden="true"><svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" focusable="false">' + svg + '</svg></span>'
              : '') +
            '<span class="hmc-option__text">' +
            '<span class="hmc-option__label">' + esc(o.label) + '</span>' +
            (o.hint ? '<span class="hmc-option__hint">' + esc(o.hint) + '</span>' : '') +
            '</span></span></label>'
          );
        }).join('') +
        '</div></fieldset>' +
        // 2026-10-27 Continue は廃止（選択肢をタップすると進む）。Back だけ残す
        '<div class="hmc-step__actions hmc-step__actions--back">' +
        '<button type="button" class="hmc-link" data-hmc-action="back">' + esc(this.text.back) + '</button>' +
        '</div></div>';
      this.hideResults();
    } else {
      var result = recommend(this.state.answers, this.config);
      this.result = result;
      var summary = questions.map(function (qq) {
        var v = this.state.answers[qq.id];
        var o = qq.options.filter(function (x) { return x.value === v; })[0];
        return o ? o.label : '';
      }, this).filter(Boolean).join(' · ');
      html =
        '<div class="hmc-step hmc-step--result">' +
        '<p class="hmc-step__progress">' + esc(summary) + '</p>' +
        '<h2 class="hmc-step__title" id="' + esc(this.titleId) + '" tabindex="-1">' + esc(this.text.resultTitle) + '</h2>' +
        '</div>';
      this.showResults(result);
      if (result) {
        track('help_me_choose_complete', {
          hmc_for: this.state.answers['for'],
          hmc_use: this.state.answers.use || '',
          hmc_taste: this.state.answers.taste || '',
          hmc_exp: this.state.answers.exp || '',
          hmc_gift_pref: this.state.answers.gift_pref || '',
          hmc_budget: this.state.answers.budget || '',
          hmc_group: result.group,
          hmc_primary: result.cards[0].handle,
          hmc_handles: result.cards.map(function (c) { return c.handle; }).join(','),
          hmc_source: this.source
        });
      }
    }

    this.stepsEl.innerHTML = html;
    if (this.footerEl) this.footerEl.hidden = step <= total;

    var scroller = this.closest('.hmc-modal__content');
    if (scroller) scroller.scrollTop = 0;
    if (moveFocus) {
      var title = this.stepsEl.querySelector('.hmc-step__title');
      if (title) title.focus({ preventScroll: true });
    }
  };

  HelpMeChoose.prototype.hideResults = function () {
    if (!this.resultsEl) return;
    this.resultsEl.hidden = true;
  };

  HelpMeChoose.prototype.showResults = function (result) {
    if (!this.resultsEl) return;
    var self = this;
    var cards = Array.prototype.slice.call(this.resultsEl.querySelectorAll('.hmc-card'));
    cards.forEach(function (c) {
      c.hidden = true;
      ['role', 'position', 'type', 'lead'].forEach(function (k) { delete c.dataset[k]; });
      self.setupCard(c, null);
    });
    if (!result) { this.resultsEl.hidden = true; return; }
    var shown = 0;
    result.cards.forEach(function (item) {
      var card = cards.filter(function (c) { return norm(c.dataset.handle) === norm(item.handle); })[0];
      if (!card) return; // テーマ側に商品が無い（非公開など）→ スキップ
      shown++;
      card.hidden = false;
      card.dataset.role = item.role;
      card.dataset.position = shown;
      card.dataset.type = item.type || '';
      if (shown === 1) card.dataset.lead = ''; // 先頭のカードを大きく表示（Just starting out は Kit、それ以外は Best match）
      var roleEl = card.querySelector('[data-hmc-role]');
      if (roleEl) roleEl.textContent = self.labels[item.role] || '';
      self.setupCard(card, item);
      self.resultsEl.appendChild(card); // 表示順に並べ替え
    });
    this.resultsEl.hidden = false;
  };

  /**
   * 結果カードを、表示する診断結果に合わせて整える（item が null なら初期状態に戻す）
   * - purchasePlan() で入れる商品を決め、価格・サイズ・One-time / Subscribe の切り替え・案内文を出す
   * - 画像・商品名のリンクにも同じバリアント（と定期便）を付ける
   *   商品ページの Seal ウィジェットは ?selling_plan= で定期便が初期選択される（2026-10-19 確認）
   */
  HelpMeChoose.prototype.setupCard = function (card, item) {
    var plan = item ? purchasePlan(item, card.dataset) : null;
    card.hmcPlan = plan;
    var purchaseEl = card.querySelector('[data-hmc-purchase]');
    var priceEl = card.querySelector('[data-hmc-price]');
    var noteEl = card.querySelector('[data-hmc-note]');
    var toggle = !!(plan && plan.mode === 'add' && plan.subscribe);

    if (purchaseEl) {
      purchaseEl.hidden = !toggle;
      var onetimeInput = purchaseEl.querySelector('[data-hmc-purchase="onetime"]');
      if (onetimeInput) onetimeInput.checked = true; // 初期は One-time（先方 10/24）
      if (toggle) {
        setText(purchaseEl.querySelector('[data-hmc-purchase-detail="onetime"]'), [plan.onetime.size, plan.onetime.price].filter(Boolean).join(' · '));
        setText(purchaseEl.querySelector('[data-hmc-purchase-detail="subscribe"]'), [plan.subscribe.size, plan.subscribe.interval, plan.subscribe.price].filter(Boolean).join(' · '));
      }
    }
    if (priceEl) {
      if (priceEl.dataset.original == null) priceEl.dataset.original = priceEl.textContent.trim();
      priceEl.hidden = toggle; // 切り替えがあるカードは各行に価格を出す
      // サイズが決まっているカードは From をやめて「$34.00 · 50g」
      var fixed = plan && plan.mode === 'add' && plan.onetime.size && plan.onetime.price;
      priceEl.textContent = fixed ? plan.onetime.price + ' · ' + plan.onetime.size : priceEl.dataset.original;
    }
    if (noteEl) {
      var note = toggle && item.note ? item.note : '';
      noteEl.textContent = note;
      noteEl.hidden = !note;
    }
    this.refreshCard(card, false);
  };

  // 今カードで選ばれている購入方法（'onetime'／'subscribe'）と、そのとき入れる商品
  function currentChoice(card) {
    var plan = card.hmcPlan;
    if (!plan || plan.mode !== 'add') return null;
    var purchaseEl = card.querySelector('[data-hmc-purchase]');
    var checked = purchaseEl && !purchaseEl.hidden ? purchaseEl.querySelector('input:checked') : null;
    var key = checked && checked.dataset.hmcPurchase === 'subscribe' && plan.subscribe ? 'subscribe' : 'onetime';
    return { key: key, option: plan[key] };
  }

  // ボタン・リンク・メッセージを今の選択に合わせる（userChange＝購入方法を切り替えたとき。Added の表示は戻す）
  HelpMeChoose.prototype.refreshCard = function (card, userChange) {
    if (!card) return;
    var choice = currentChoice(card);
    var btn = card.querySelector('[data-hmc-add]');
    var viewCart = card.querySelector('[data-hmc-view-cart]');
    if (btn) {
      var ok = !!(choice && choice.option.variant && choice.option.available);
      btn.disabled = !ok;
      btn.classList.remove('is-added', 'loading');
      btn.removeAttribute('aria-busy');
      setText(btn, ok ? this.text.add : this.text.soldOut);
    }
    if (viewCart && !userChange) viewCart.hidden = true; // 結果が変わったときだけ隠す（切り替えでは残す）
    this.cardMessage(card, '');

    // 画像・商品名のリンクに、今の選択（バリアント・定期便）を付ける
    var query = [];
    if (choice && choice.option.variant) query.push('variant=' + encodeURIComponent(choice.option.variant));
    if (choice && choice.key === 'subscribe' && choice.option.plan) query.push('selling_plan=' + encodeURIComponent(choice.option.plan));
    Array.prototype.forEach.call(card.querySelectorAll('[data-hmc-view]'), function (a) {
      if (!a.dataset.baseHref) a.dataset.baseHref = a.getAttribute('href');
      var base = a.dataset.baseHref;
      a.setAttribute('href', query.length ? base + (base.indexOf('?') >= 0 ? '&' : '?') + query.join('&') : base);
    });
  };

  HelpMeChoose.prototype.cardMessage = function (card, message) {
    var errorEl = card.querySelector('[data-hmc-error]');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = !message;
  };

  /**
   * 結果カードから直接カートに入れる（Dawn の product-form と同じ /cart/add。sections でヘッダーのカート数も受け取る）
   * - 本番のカートはドロワーではなく cart-notification。診断のモーダルと重なるので通知は開かず、
   *   ボタンを「Added ✓」にしてカード内に View cart を出す（10/26 回答）
   * - fetch の前に saveAttr → snippets/hmc-attribution.liquid が /cart/add を拾い、help_me_choose_add_to_cart とカート属性を記録する
   */
  HelpMeChoose.prototype.addToCart = function (card, btn) {
    var choice = card ? currentChoice(card) : null;
    if (!choice || !choice.option.variant || !choice.option.available || btn.getAttribute('aria-busy') === 'true') return;
    var self = this;
    var option = choice.option;
    this.trackCard(card, 'add', choice.key);

    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('loading');
    btn.classList.remove('is-added');
    setText(btn, this.text.adding);
    this.cardMessage(card, '');

    var line = { id: Number(option.variant), quantity: 1 };
    if (choice.key === 'subscribe' && option.plan) line.selling_plan = Number(option.plan);
    var routes = window.routes || {};
    var url = (routes.cart_add_url || '/cart/add') + '.js';
    window.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [line], sections: 'cart-icon-bubble', sections_url: window.location.pathname })
    })
      .then(function (res) { return res.json(); })
      .then(function (json) {
        if (!json || json.status) throw json || {};
        updateCartBubble(json.sections);
        // Dawn のカート関連（カートページの再描画など）にも知らせる
        if (typeof window.publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          window.publish(PUB_SUB_EVENTS.cartUpdate, { source: 'help-me-choose', productVariantId: line.id, cartData: json });
        }
        btn.classList.add('is-added');
        setText(btn, self.text.added);
        var viewCart = card.querySelector('[data-hmc-view-cart]');
        if (viewCart) viewCart.hidden = false;
        var status = card.querySelector('[data-hmc-status]');
        if (status) status.textContent = self.text.addedStatus;
      })
      .catch(function (err) {
        setText(btn, self.text.add);
        // Shopify のエラー（在庫不足など）は description に理由が入る。通信エラーなどは共通の文
        self.cardMessage(card, (err && typeof err.description === 'string' && err.description) || self.text.addError);
      })
      .then(function () {
        btn.classList.remove('loading');
        btn.removeAttribute('aria-busy');
      });
  };

  // ヘッダーのカート数（Dawn の #cart-icon-bubble）を、/cart/add が返したセクションの HTML で差し替える
  function updateCartBubble(sections) {
    var html = sections && sections['cart-icon-bubble'];
    var bubble = document.getElementById('cart-icon-bubble');
    if (!html || !bubble) return;
    var section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (section) bubble.innerHTML = section.innerHTML;
  }

  function setText(el, text) {
    if (!el) return;
    var span = el.querySelector('[data-hmc-label]');
    (span || el).textContent = text;
  }

  if (!window.customElements.get('help-me-choose')) {
    window.customElements.define('help-me-choose', HelpMeChoose);
  }
})();
